import classNames from 'classnames';
import React, { memo, SyntheticEvent, useCallback, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { flushSync } from 'react-dom';

type Props = {
  children: React.ReactNode;

  className?: string;
  tooltipClassName?: string;
  wrapperClassName?: string;
  showHover?: boolean;
  text?: string; // customText instead of children
};

/** https://stackoverflow.com/questions/143815/determine-if-an-html-elements-content-overflows/143889#143889 */
const checkOverflow = (el: HTMLDivElement, showHover: boolean) => {
  if (showHover) {
    return true;
  }
  const curOverflow = el.style.overflow;

  if (!curOverflow || curOverflow === 'visible') el.style.overflow = 'hidden';

  const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;

  el.style.overflow = curOverflow;

  return isOverflowing;
};

/**
 * a component for displaying any text but if given text is lengthy, it shows a tooltip over the text
 */
export const UiTextTooltip: React.FC<Props> = memo(
  ({ children, text, showHover = false, className, tooltipClassName, wrapperClassName }: Props) => {
    const [showTip, setShowTip] = useState(false);

    const onMouseEnter = useCallback(
      (e: SyntheticEvent<HTMLDivElement>) => {
        if (checkOverflow(e.target as HTMLDivElement, showHover)) {
          flushSync(() => {
            setShowTip(true);
          });
        }
      },
      [showHover]
    );

    const onMouseLeave = useCallback(() => {
      setShowTip(false);
    }, []);

    return (
      <div
        role="presentation"
        className={classNames('r-tooltip', wrapperClassName)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <span className={classNames('r-tooltip__text', className)} data-tip={showTip ? text || children : undefined}>
          {children}
        </span>
        {showTip && (
          <ReactTooltip className={classNames('r-tooltip__tooltip', tooltipClassName)} effect="solid" delayHide={50} />
        )}
      </div>
    );
  }
);
